import { IconLayoutGrid, IconPlus } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
	useDeleteTaskTemplates,
	useTaskTemplatesPaged,
} from "@/common/artemis-operations/task-templates";
import {
	DELETE_RESOURCE_ERROR_FAILED,
	DELETE_RESOURCE_INFO_COMPLETED,
} from "@/common/messages";
import type { TaskTemplate } from "@/common/model";
import {
	fromTaskTemplatePlaceholder,
	type PlaceholderValue,
	substituteVariables,
} from "@/common/placeholders";
import { useNotify } from "@/common/use-notify";
import { BasePageContainer, CustomIcon, QueryGuard } from "@/components/base";
import { FillPlaceholdersWizard } from "@/components/placeholders/fill-placeholders-wizard";
import { TaskTemplateTable } from "@/components/task-templates/task-template-table";
import { TemplateGalleryBrowser } from "@/components/template-gallery/template-gallery-browser";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";
import { useWizards } from "@/contexts/wizard-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/task-templates/",
)({
	component: TaskTemplates,
	// `?importTemplate=<url>` — set by the gallery deep link (see `/import-template`)
	// to auto-open the customize step for that template. `?search=<id>` pre-filters the
	// list — templates have no detail page, so links to one land here.
	validateSearch: (
		search: Record<string, unknown>,
	): { importTemplate?: string; search?: string } => ({
		importTemplate:
			typeof search.importTemplate === "string"
				? search.importTemplate
				: undefined,
		search: typeof search.search === "string" ? search.search : undefined,
	}),
});

/**
 * The template with its `{{key}}` placeholders replaced by `values` in every text
 * field. With no values (editing the template itself) the text is left as-is.
 */
function fillPlaceholders(
	template: TaskTemplate,
	values?: Record<string, PlaceholderValue>,
): TaskTemplate {
	if (!values) return template;
	const sub = <T extends string | null | undefined>(text: T): T =>
		(text == null ? text : substituteVariables(text, values)) as T;
	return {
		...template,
		name: sub(template.name),
		description: sub(template.description),
		instructions: sub(template.instructions),
		verificationCriteria: sub(template.verificationCriteria),
	};
}

/**
 * Turns a saved template into the wizard's pre-fill shape. This is the whole
 * template-to-task "clone": the backend keeps no link between the two.
 */
function toInitialValues(template: TaskTemplate) {
	return {
		name: template.name,
		description: template.description ?? "",
		instructions: template.instructions,
		placeholders: template.placeholders.map(fromTaskTemplatePlaceholder),
		projectId: template.projectId,
		deploymentIds: template.deploymentIds,
		environmentIds: template.environmentIds,
		assigneeId: template.assigneeId,
		assigneeType: template.assigneeType,
		verificationCriteria: template.verificationCriteria,
	};
}

/**
 * Turns a saved template into the job wizard's pre-fill shape. Jobs only ever run as
 * an agent, so a template assigned to a user leaves the agent unset for the user to pick.
 */
function toJobInitialValues(template: TaskTemplate) {
	return {
		name: template.name,
		description: template.description ?? "",
		instructions: template.instructions,
		projectId: template.projectId,
		deploymentIds: template.deploymentIds,
		environmentIds: template.environmentIds,
		agentId:
			template.assigneeType === "AGENT" ? template.assigneeId : undefined,
	};
}

function TaskTemplates() {
	const { workspaceId } = Route.useParams();
	const { importTemplate, search } = Route.useSearch();
	const navigate = useNavigate();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const notify = useNotify();
	const { openTaskCreateWizard, openJobCreateWizard, openTaskTemplateWizard } =
		useWizards();
	// A gallery deep link lands with the gallery already open on its customize step.
	const [galleryOpen, setGalleryOpen] = useState(importTemplate != null);
	// A template with placeholders being used: its values are asked for first.
	const [filling, setFilling] = useState<{
		template: TaskTemplate;
		use: "task" | "job";
	}>();

	const applyTemplate = (
		template: TaskTemplate,
		use: "task" | "job",
		values?: Record<string, PlaceholderValue>,
	) => {
		const filled = fillPlaceholders(template, values);
		if (use === "task") openTaskCreateWizard(toInitialValues(filled));
		else openJobCreateWizard(toJobInitialValues(filled));
	};
	const startUsingTemplate = (template: TaskTemplate, use: "task" | "job") => {
		if (template.placeholders.length > 0) setFilling({ template, use });
		else applyTemplate(template, use);
	};

	const list = useTaskTemplatesPaged(workspaceId, search);
	const { mutate: deleteTaskTemplates } = useDeleteTaskTemplates();

	useEffect(() => {
		setTitle("Templates");
		setActions([
			{
				type: "button",
				id: "browse-gallery",
				tooltip: "Template gallery",
				icon: <CustomIcon icon={IconLayoutGrid} />,
				onClick: () => setGalleryOpen(true),
			},
			{
				type: "button",
				id: "create",
				tooltip: "New template",
				icon: <CustomIcon icon={IconPlus} />,
				onClick: () => openTaskTemplateWizard(),
			},
		]);
		return () => setActions([]);
	}, [setTitle, setActions, openTaskTemplateWizard]);

	return (
		<QueryGuard isPending={list.loading} isError={list.isError}>
			<TemplateGalleryBrowser
				open={galleryOpen}
				onClose={() => setGalleryOpen(false)}
				workspaceId={workspaceId}
				autoImportUrl={importTemplate}
				onAutoImportHandled={() =>
					navigate({ to: ".", search: {}, replace: true })
				}
			/>
			<FillPlaceholdersWizard
				open={filling != null}
				title={filling?.use === "job" ? "Schedule job" : "Create task"}
				templateName={filling?.template.name ?? ""}
				fields={(filling?.template.placeholders ?? []).map(
					fromTaskTemplatePlaceholder,
				)}
				onSubmit={(values) => {
					if (filling) applyTemplate(filling.template, filling.use, values);
					setFilling(undefined);
				}}
				onCancel={() => setFilling(undefined)}
			/>
			<BasePageContainer>
				<TaskTemplateTable
					workspaceId={workspaceId}
					data={list.rows}
					loading={list.isFetching}
					totalCount={list.totalCount}
					hasNextPage={list.hasNextPage}
					hasPreviousPage={list.hasPreviousPage}
					pageIndex={list.pageIndex}
					onNextPage={list.onNextPage}
					onPreviousPage={list.onPreviousPage}
					pageSize={50}
					manualSorting
					initialSorting={list.sort}
					onSortingChange={list.setSort}
					search={list.search}
					onSearchChange={list.setSearch}
					onRefresh={() => list.refetch()}
					onCreateTask={(template) => startUsingTemplate(template, "task")}
					onScheduleJob={(template) => startUsingTemplate(template, "job")}
					onEditTemplate={(template) =>
						openTaskTemplateWizard(toInitialValues(template), template.id)
					}
					onDeleteTemplates={(selected) => {
						deleteTaskTemplates(
							selected.map((t) => t.id as string),
							{
								onSuccess: () => notify.success(DELETE_RESOURCE_INFO_COMPLETED),
								onError: () => notify.error(DELETE_RESOURCE_ERROR_FAILED),
							},
						);
					}}
				/>
			</BasePageContainer>
		</QueryGuard>
	);
}
